import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { useAuth } from '../auth';
import ObiettiviWizard from '../components/ObiettiviWizard';
import SsmateAgenteCard from '../components/SsmateAgenteCard';
import IndagaAgenteCard from '../components/IndagaAgenteCard';

const card = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 18, marginBottom: 16 };
const nf = (n) => (n || 0).toLocaleString('it-IT');

export default function StrumentiPage() {
  const { can, user } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [obiettivi, setObiettivi] = useState([]);
  const [delOb, setDelOb] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [zone, setZone] = useState([]);     // [{tipo, nome}]
  const [orig, setOrig] = useState([]);
  const [regioni, setRegioni] = useState([]);
  const [province, setProvince] = useState([]);
  const [selReg, setSelReg] = useState('');
  const [selProv, setSelProv] = useState('');
  const [saving, setSaving] = useState(false);
  // Blacklist / opt-out
  const [blTotal, setBlTotal] = useState(0);
  const [blItems, setBlItems] = useState([]);
  const [blInput, setBlInput] = useState('');
  const [blMotivo, setBlMotivo] = useState('');
  const [blBusy, setBlBusy] = useState(false);
  // Normalizza prefisso
  const [normInfo, setNormInfo] = useState(null);     // {sospetti_totali, sistemabili_completi, solo_provincia, campione}
  const [normBusy, setNormBusy] = useState(false);
  const [normDettagli, setNormDettagli] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/settings`).then(r => {
      const z = r.data?.zone_escluse || []; setZone(z); setOrig(z);
    }).catch(() => {});
    axios.get(`${API}/dashboard/aree-counts`).then(r => { setRegioni(r.data?.regioni || []); setProvince(r.data?.province || []); }).catch(() => {});
    axios.get(`${API}/obiettivi`).then(r => setObiettivi(r.data || [])).catch(() => {});
    axios.get(`${API}/blacklist`).then(r => { setBlTotal(r.data?.total || 0); setBlItems(r.data?.items || []); }).catch(() => {});
    axios.get(`${API}/strumenti/normalizza-prefisso/anteprima`).then(r => setNormInfo(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const countOf = (z) => {
    if (z.tipo === 'regione') return regioni.find(x => x.nome === z.nome)?.count || 0;
    return province.find(x => x.nome === z.nome)?.count || 0;
  };
  const has = (tipo, nome) => zone.some(z => z.tipo === tipo && z.nome === nome);
  const addZona = (tipo, nome) => { if (!nome || has(tipo, nome)) return; setZone(z => [...z, { tipo, nome }]); };
  const removeZona = (tipo, nome) => setZone(z => z.filter(x => !(x.tipo === tipo && x.nome === nome)));

  const doDeleteOb = async () => {
    if (!delOb) return;
    setDelBusy(true);
    try {
      await axios.delete(`${API}/obiettivi/${delOb.id}`);
      toast.success('Priorità annullata');
      setDelOb(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setDelBusy(false);
  };


  const addBlacklist = async () => {
    const numeri = blInput.split(/[\s,;]+/).filter(Boolean);
    if (numeri.length === 0) { toast.info('Inserisci almeno un numero'); return; }
    setBlBusy(true);
    try {
      const r = await axios.post(`${API}/blacklist`, { numeri, motivo: blMotivo });
      toast.success(`${r.data.added} aggiunti in blacklist (${r.data.numeri_marcati} numeri marcati in archivio)`);
      setBlInput(''); setBlMotivo(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setBlBusy(false);
  };
  const delBlacklist = async (numero) => {
    try { await axios.delete(`${API}/blacklist/${encodeURIComponent(numero)}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const applicaNorm = async () => {
    if (!window.confirm('Adattare provincia/comune al prefisso telefonico sui record incoerenti? I valori originali vengono conservati.')) return;
    setNormBusy(true);
    try {
      const r = await axios.post(`${API}/strumenti/normalizza-prefisso/applica`);
      toast.success(`Sistemati ${r.data.aggiornati} record (${r.data.completi} provincia+comune, ${r.data.da_rivedere} solo provincia → da rivedere)`);
      const a = await axios.get(`${API}/strumenti/normalizza-prefisso/anteprima`);
      setNormInfo(a.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore normalizzazione');
    } finally { setNormBusy(false); }
  };

  const dirty = JSON.stringify(zone) !== JSON.stringify(orig);
  const totImpatto = zone.reduce((s, z) => s + countOf(z), 0);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { zone_escluse: zone });
      setOrig(zone);
      toast.success('Esclusioni salvate');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setSaving(false);
  };

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>🛠 Strumenti</h1>
      <p style={{ margin: '0 0 18px', color: 'var(--muted)', fontSize: 13 }}>
        Leve di lavorazione: dove dare priorità e quali zone escludere dalle verifiche.
      </p>

      {/* AI SSMATE (admin o permesso ssmate) */}
      {can('ssmate') && <SsmateAgenteCard />}

      {/* Agente Indaga — completa indirizzi (solo admin) */}
      {user?.ruolo === 'admin' && <IndagaAgenteCard />}

      {/* Priorità e esclusioni sono ora per-agente: gestiscile dalle spie in sidebar
          (ingranaggio → icona priorità di ciascun agente). */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          <b>Priorità aree</b> ed <b>esclusione zone</b> sono ora gestite <b>per singolo agente</b>:
          aprile dalla <b>sidebar</b> → ingranaggio delle spie → icona priorità dell'agente.
        </div>
      </div>

      {/* Blacklist / opt-out */}
      <div style={card}>
        <h2 style={{ margin: '0 0 4px', fontSize: 15 }}>Blacklist / opt-out</h2>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
          Numeri da <b>non contattare mai</b>: esclusi da verifiche 456 ed export (anche dai lead).
          In blacklist: <b>{nf(blTotal)}</b>.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <textarea
            value={blInput}
            onChange={e => setBlInput(e.target.value)}
            placeholder="Incolla i numeri (uno per riga, o separati da spazio/virgola)"
            style={{ flex: '1 1 320px', minHeight: 70, fontSize: 13, padding: 8 }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input value={blMotivo} onChange={e => setBlMotivo(e.target.value)} placeholder="Motivo (opzionale)" style={{ width: 200 }} />
            <button className="btn btn-primary" disabled={blBusy} onClick={addBlacklist}>
              {blBusy ? 'Aggiungo…' : 'Aggiungi in blacklist'}
            </button>
          </div>
        </div>

        {blItems.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Ultimi inseriti{blTotal > blItems.length ? ` (${nf(blItems.length)} di ${nf(blTotal)})` : ''}:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 160, overflowY: 'auto' }}>
              {blItems.map(b => (
                <span key={b.numero_pulito} title={b.motivo || ''} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '3px 9px',
                  background: 'rgba(239,68,68,.08)', color: 'var(--err)', borderRadius: 16, fontSize: 12,
                }}>
                  <code>{b.numero_pulito}</code>
                  <button onClick={() => delBlacklist(b.numero_pulito)} style={{ background: 'none', border: 'none', color: 'var(--err)', cursor: 'pointer', fontWeight: 800 }}>✕</button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Normalizza provincia/comune al prefisso */}
      <div style={card}>
        <h2 style={{ margin: '0 0 4px', fontSize: 15 }}>📍 Normalizza provincia/comune al prefisso</h2>
        <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: 13 }}>
          Alcuni indirizzi importati hanno la provincia incoerente col numero di telefono
          (es. numero di Benevento con indirizzo a Roma). Questo strumento adatta provincia e —
          per i prefissi di città (06→Roma, 02→Milano…) — anche il comune. Gli altri restano
          segnalati come "da rivedere". I valori originali vengono conservati.
        </p>
        {!normInfo ? (
          <div style={{ color: 'var(--faint)', fontSize: 13 }}>Calcolo in corso…</div>
        ) : normInfo.sospetti_totali === 0 ? (
          <div style={{ color: 'var(--ok)', fontSize: 13 }}>Nessun record con provincia incoerente. ✓</div>
        ) : (
          <>
            <div style={{ fontSize: 14, marginBottom: 10 }}>
              <b>{normInfo.sospetti_totali}</b> record incoerenti —
              {' '}<b>{normInfo.sistemabili_completi}</b> sistemabili (provincia+comune),
              {' '}<b>{normInfo.solo_provincia}</b> solo provincia (resteranno da rivedere).
            </div>
            <button className="btn btn-sm" style={{ marginRight: 8 }} onClick={() => setNormDettagli(v => !v)}>
              {normDettagli ? 'Nascondi dettagli' : 'Anteprima dettagli'}
            </button>
            <button className="btn btn-sm btn-primary" disabled={normBusy} onClick={applicaNorm}>
              {normBusy ? 'Applico…' : 'Applica normalizzazione'}
            </button>
            {normDettagli && (
              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table style={{ width: '100%', fontSize: 12 }}>
                  <thead><tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                    <th>Numero</th><th>Provincia (attuale→proposta)</th><th>Comune (attuale→proposto)</th>
                  </tr></thead>
                  <tbody>
                    {normInfo.campione.map((c, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--surface-2)' }}>
                        <td><code>{c.numero}</code></td>
                        <td>{c.provincia_attuale || '—'} → <b>{c.provincia_proposta || '—'}</b></td>
                        <td>{c.comune_attuale || '—'} → <b>{c.comune_proposto || '(da rivedere)'}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {delOb && (
        <div className="nt-overlay" onClick={() => !delBusy && setDelOb(null)}>
          <div className="nt-detail" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="nt-detail-head">
              <span>Annulla priorità</span>
              <button className="ob-x" onClick={() => !delBusy && setDelOb(null)}>✕</button>
            </div>
            <div className="nt-detail-body">
              <p style={{ fontSize: 14, marginTop: 0 }}>
                Annullare la priorità su <b>{delOb.nome}</b> ({delOb.tipo})?
              </p>
              <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                L'obiettivo viene rimosso; i numeri di quell'area tornano alla lavorazione normale.
              </p>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} disabled={delBusy} onClick={() => setDelOb(null)}>Annulla</button>
                <button className="btn" style={{ flex: 1, background: 'var(--err)', color: '#fff', border: 'none' }} disabled={delBusy} onClick={doDeleteOb}>
                  {delBusy ? '…' : 'Rimuovi priorità'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ObiettiviWizard open={wizardOpen} onClose={() => { setWizardOpen(false); load(); }} />
    </div>
  );
}
